import React, { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import {
  EditIcon,
  ShieldCheckIcon,
  PlusIcon,
  DeleteIcon,
} from "../../../../Icons";
import { updatePatientAuthorizations } from "../../../../services/patientService";
import {
  buildAuthorizations,
  buildAuthorizationsPayload,
  emptyAuthorization,
  emptyAuthDiscipline,
  validateAuthorizations,
  AUTH_DISCIPLINE_OPTIONS,
  availableAuthDisciplineOptions,
} from "../../authorizationForm.shared";
import { UNAUTHORIZED_TEXT } from "../../visitAuthorization";
import { CollapseToggle } from "../CollapsibleSection";
import {
  RowTextField,
  RowSelectField,
  RowDateField,
  AddRowButton,
} from "../MultiRowFields";
import { PATIENT_SECTION_TONE, PATIENT_THEME } from "../../patientTheme";

const tone = PATIENT_SECTION_TONE;

const dash = (v) => {
  const s = String(v ?? "").trim();
  return s || "—";
};

/**
 * Authorizations — one auth period can hold many discipline lines
 * (expandable parent → nested discipline table).
 */
const PatientAuthorizationsSection = ({ patient, chartId, canEdit, onSaved }) => {
  const [sectionOpen, setSectionOpen] = useState(true);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [rows, setRows] = useState(() => buildAuthorizations(patient));
  const [expanded, setExpanded] = useState(() => new Set([0]));

  useEffect(() => {
    if (!editing) setRows(buildAuthorizations(patient));
  }, [patient, editing, chartId]);

  const savedRows = useMemo(() => buildAuthorizations(patient), [patient]);
  const displayRows = editing ? rows : savedRows;
  const isEmpty = displayRows.length === 0;

  useEffect(() => {
    if (displayRows.length === 0) {
      setExpanded(new Set());
      return;
    }
    setExpanded((prev) => {
      if (prev.size) return prev;
      return new Set([0]);
    });
  }, [displayRows.length]);

  const toggleExpanded = (index) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      return next;
    });
  };

  const startEdit = (withBlank = false) => {
    const next = buildAuthorizations(patient);
    const initial =
      withBlank || next.length === 0 ? [emptyAuthorization()] : next;
    setRows(initial);
    setExpanded(new Set(initial.map((_, i) => i)));
    setEditing(true);
    setSectionOpen(true);
  };

  const cancelEdit = () => {
    setRows(buildAuthorizations(patient));
    setEditing(false);
  };

  const updateAuth = (index, key, value) => {
    setRows((prev) =>
      prev.map((row, i) => (i === index ? { ...row, [key]: value } : row)),
    );
  };

  const updateDiscipline = (authIndex, discIndex, key, value) => {
    setRows((prev) =>
      prev.map((row, i) => {
        if (i !== authIndex) return row;
        return {
          ...row,
          disciplines: (row.disciplines || []).map((d, j) =>
            j === discIndex ? { ...d, [key]: value } : d,
          ),
        };
      }),
    );
  };

  const addDiscipline = (authIndex) => {
    setRows((prev) =>
      prev.map((row, i) =>
        i === authIndex
          ? {
              ...row,
              disciplines: [...(row.disciplines || []), emptyAuthDiscipline()],
            }
          : row,
      ),
    );
    setExpanded((prev) => new Set(prev).add(authIndex));
  };

  const removeDiscipline = (authIndex, discIndex) => {
    setRows((prev) =>
      prev.map((row, i) => {
        if (i !== authIndex) return row;
        const next = (row.disciplines || []).filter((_, j) => j !== discIndex);
        return {
          ...row,
          disciplines: next.length ? next : [emptyAuthDiscipline()],
        };
      }),
    );
  };

  const removeAuth = (index) => {
    setRows((prev) => prev.filter((_, i) => i !== index));
    setExpanded((prev) => {
      const next = new Set();
      prev.forEach((i) => {
        if (i < index) next.add(i);
        else if (i > index) next.add(i - 1);
      });
      return next;
    });
  };

  const addAuth = () => {
    setRows((prev) => {
      const next = [...prev, emptyAuthorization()];
      setExpanded((e) => new Set(e).add(next.length - 1));
      return next;
    });
    setSectionOpen(true);
  };

  const handleSave = async () => {
    const error = validateAuthorizations(rows);
    if (error) {
      toast.error(error);
      return;
    }
    if (!patient?.id) {
      toast.error("Patient is missing; reload the chart and try again");
      return;
    }
    setSaving(true);
    try {
      await updatePatientAuthorizations(patient.id, {
        authorizations: buildAuthorizationsPayload(rows),
        ...(chartId ? { chartId: Number(chartId) } : {}),
      });
      toast.success("Authorizations saved");
      if (onSaved) {
        await onSaved({ silent: true });
      }
      setEditing(false);
    } catch (err) {
      toast.error(err.message || "Failed to save authorizations");
    } finally {
      setSaving(false);
    }
  };

  return (
    <section
      className={`bg-white rounded-xl border shadow-sm overflow-hidden ${tone.wrap}`}
    >
      <div
        className={`flex items-center gap-2 h-11 px-4 border-b box-border ${tone.head}`}
      >
        <CollapseToggle
          open={sectionOpen}
          onToggle={() => setSectionOpen((o) => !o)}
          labelOpen="Collapse Authorizations"
          labelClosed="Expand Authorizations"
        />
        <span className={`w-0.5 h-4 rounded-full shrink-0 ${tone.bar}`} aria-hidden />
        <span className={`shrink-0 ${tone.icon}`}>
          <ShieldCheckIcon size={15} />
        </span>
        <h3
          className={`text-xs font-semibold uppercase tracking-wide flex-1 min-w-0 truncate ${tone.title}`}
        >
          Authorizations
        </h3>

        {canEdit && !editing && !isEmpty && (
          <button
            type="button"
            onClick={() => startEdit(false)}
            className={`inline-flex items-center justify-center h-7 w-7 bg-white border border-slate-200 rounded hover:bg-slate-50 transition-colors ${PATIENT_THEME.section.icon}`}
            aria-label="Edit"
            title="Edit"
          >
            <EditIcon size={13} />
          </button>
        )}

        {editing && (
          <div className="flex items-center gap-1.5 h-7">
            <button
              type="button"
              onClick={cancelEdit}
              disabled={saving}
              className="inline-flex items-center h-7 px-2.5 text-xs font-medium text-slate-700 bg-white border border-slate-300 rounded-md hover:bg-slate-50 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={saving}
              className={`inline-flex items-center gap-1.5 h-7 px-2.5 text-xs font-medium bg-white border rounded-md hover:bg-opacity-60 disabled:opacity-50 ${PATIENT_THEME.button.outline}`}
            >
              {saving && (
                <span className="w-3 h-3 border-2 border-current border-t-transparent rounded-full animate-spin" />
              )}
              Save
            </button>
          </div>
        )}
      </div>

      {sectionOpen && (
        <div className="p-4 bg-slate-50/40">
          {isEmpty ? (
            <div className="flex flex-col items-center justify-center gap-3 py-12 px-4 rounded-xl border border-dashed border-slate-200 bg-white">
              <div
                className={`inline-flex h-11 w-11 items-center justify-center rounded-full ${PATIENT_THEME.accentSoft}`}
              >
                <ShieldCheckIcon size={20} />
              </div>
              <div className="text-center max-w-sm">
                <p className="text-sm font-semibold text-slate-700">
                  No authorizations yet
                </p>
                <p className="text-[12px] text-slate-500 mt-1">
                  Add one auth period, then attach disciplines (RN/LVN, PT/PTA,
                  CG/CNA…) under it.
                </p>
              </div>
              {canEdit && (
                <button
                  type="button"
                  onClick={() => (editing ? addAuth() : startEdit(true))}
                  className={`inline-flex items-center justify-center gap-1.5 h-9 px-4 text-xs font-medium border rounded-md ${PATIENT_THEME.button.outline}`}
                >
                  <PlusIcon size={14} />
                  Add Authorization
                </button>
              )}
            </div>
          ) : editing ? (
            <div className="space-y-3">
              {displayRows.map((row, authIndex) => {
                const open = expanded.has(authIndex);
                return (
                  <div
                    key={authIndex}
                    className="rounded-xl border border-slate-200 bg-white overflow-hidden"
                  >
                    <div className="flex items-start gap-2 px-3 py-2.5 border-b border-slate-100 bg-slate-50/80">
                      <button
                        type="button"
                        onClick={() => toggleExpanded(authIndex)}
                        className="mt-6 inline-flex h-7 w-7 items-center justify-center rounded border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 shrink-0"
                        aria-label={open ? "Collapse" : "Expand"}
                        title={open ? "Collapse" : "Expand"}
                      >
                        <span className="text-sm font-bold leading-none">
                          {open ? "−" : "+"}
                        </span>
                      </button>
                      <div className="grid flex-1 grid-cols-2 md:grid-cols-4 gap-2.5 min-w-0">
                        <RowTextField
                          label={`Auth ${authIndex + 1} · Auth #`}
                          value={row.authNumber}
                          onChange={(e) =>
                            updateAuth(authIndex, "authNumber", e.target.value)
                          }
                          placeholder="Auth #"
                          maxLength={100}
                          className="min-w-0"
                        />
                        <RowDateField
                          label="From"
                          value={row.fromDate}
                          onChange={(v) =>
                            updateAuth(authIndex, "fromDate", v)
                          }
                          className="min-w-0"
                        />
                        <RowDateField
                          label="To"
                          value={row.toDate}
                          onChange={(v) => updateAuth(authIndex, "toDate", v)}
                          className="min-w-0"
                        />
                        <RowTextField
                          label="Notes"
                          value={row.notes}
                          onChange={(e) =>
                            updateAuth(authIndex, "notes", e.target.value)
                          }
                          placeholder="Notes"
                          maxLength={2000}
                          className="min-w-0"
                        />
                      </div>
                      <button
                        type="button"
                        onClick={() => removeAuth(authIndex)}
                        className="mt-6 inline-flex items-center justify-center h-9 w-9 text-red-600 border border-red-200 bg-white hover:bg-red-50 rounded-md shrink-0"
                        title="Delete authorization"
                        aria-label="Delete authorization"
                      >
                        <DeleteIcon size={14} />
                      </button>
                    </div>

                    {open && (
                      <div className="p-3 space-y-2 bg-white">
                        <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-500 px-0.5">
                          Disciplines
                        </p>
                        {(row.disciplines || []).map((disc, discIndex) => (
                          <div
                            key={discIndex}
                            className="grid grid-cols-[minmax(0,1.4fr)_minmax(0,0.9fr)_auto] items-end gap-2.5 rounded-lg border border-slate-100 bg-slate-50/50 px-3 py-2.5"
                          >
                            <RowSelectField
                              label="Discipline"
                              value={disc.discipline}
                              onChange={(e) =>
                                updateDiscipline(
                                  authIndex,
                                  discIndex,
                                  "discipline",
                                  e.target.value,
                                )
                              }
                              options={availableAuthDisciplineOptions(
                                row.disciplines,
                                discIndex,
                              )}
                              placeholder="(Select)"
                              className="min-w-0"
                            />
                            <RowTextField
                              label="Authorized"
                              value={disc.authorizedUnits}
                              onChange={(e) =>
                                updateDiscipline(
                                  authIndex,
                                  discIndex,
                                  "authorizedUnits",
                                  e.target.value.replace(/[^\d]/g, ""),
                                )
                              }
                              placeholder="0"
                              maxLength={4}
                              className="min-w-0"
                            />
                            <button
                              type="button"
                              onClick={() =>
                                removeDiscipline(authIndex, discIndex)
                              }
                              className="inline-flex items-center justify-center h-9 w-9 mb-0.5 text-red-600 border border-red-200 bg-white hover:bg-red-50 rounded-md shrink-0"
                              title="Remove discipline"
                              aria-label="Remove discipline"
                            >
                              <DeleteIcon size={14} />
                            </button>
                          </div>
                        ))}
                        <div className="pt-1">
                          {(row.disciplines || []).filter((d) => d.discipline)
                            .length < AUTH_DISCIPLINE_OPTIONS.length ? (
                            <AddRowButton
                              label="Add Discipline"
                              onClick={() => addDiscipline(authIndex)}
                            />
                          ) : null}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
              <div className="flex justify-center pt-1">
                <AddRowButton label="Add Authorization" onClick={addAuth} />
              </div>
            </div>
          ) : (
            <div className="space-y-2">
              {displayRows.map((row, authIndex) => {
                const open = expanded.has(authIndex);
                const disciplines = row.disciplines || [];
                return (
                  <div
                    key={authIndex}
                    className="rounded-xl border border-slate-200 bg-white overflow-hidden"
                  >
                    <button
                      type="button"
                      onClick={() => toggleExpanded(authIndex)}
                      className="w-full flex items-center gap-2 px-3 py-2.5 text-left hover:bg-slate-50/80 transition-colors"
                    >
                      <span
                        className="inline-flex h-6 w-6 items-center justify-center rounded border border-slate-200 bg-white text-slate-600 shrink-0 text-sm font-bold"
                        aria-hidden
                      >
                        {open ? "−" : "+"}
                      </span>
                      <div className="grid flex-1 grid-cols-2 sm:grid-cols-4 gap-x-3 gap-y-1 min-w-0 text-sm">
                        <div className="min-w-0">
                          <p className="text-[9px] font-semibold uppercase tracking-wide text-slate-500">
                            Auth {authIndex + 1}
                          </p>
                          <p className="font-semibold text-slate-900 truncate">
                            {dash(row.authNumber)}
                          </p>
                        </div>
                        <div className="min-w-0">
                          <p className="text-[9px] font-semibold uppercase tracking-wide text-slate-500">
                            From
                          </p>
                          <p className="text-slate-700 tabular-nums whitespace-nowrap">
                            {dash(row.fromDate)}
                          </p>
                        </div>
                        <div className="min-w-0">
                          <p className="text-[9px] font-semibold uppercase tracking-wide text-slate-500">
                            To
                          </p>
                          <p className="text-slate-700 tabular-nums whitespace-nowrap">
                            {dash(row.toDate)}
                          </p>
                        </div>
                        <div className="min-w-0">
                          <p className="text-[9px] font-semibold uppercase tracking-wide text-slate-500">
                            Notes
                          </p>
                          <p className="text-slate-700 truncate">
                            {dash(row.notes)}
                          </p>
                        </div>
                      </div>
                    </button>

                    {open && (
                      <div className="border-t border-slate-100 overflow-x-auto">
                        <table className="w-full min-w-[24rem] text-sm">
                          <thead>
                            <tr className="bg-slate-100/90 text-left text-[11px] uppercase tracking-wide text-slate-600">
                              <th className="px-3 py-2 font-semibold">
                                Discipline
                              </th>
                              <th className="px-3 py-2 font-semibold">
                                Authorized
                              </th>
                              <th className="px-3 py-2 font-semibold">
                                Applied
                              </th>
                              <th className="px-3 py-2 font-semibold">
                                Remaining
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {disciplines.map((disc, discIndex) => {
                              const remaining = disc.remainingUnits;
                              const under =
                                remaining != null &&
                                disc.authorizedUnits !== "" &&
                                Number(remaining) > 0 &&
                                Number(remaining) <
                                  Number(disc.authorizedUnits || 0);
                              return (
                                <tr
                                  key={discIndex}
                                  className="border-t border-slate-100 hover:bg-slate-50/70"
                                >
                                  <td className="px-3 py-2 text-slate-800 font-medium">
                                    {dash(disc.discipline)}
                                  </td>
                                  <td className="px-3 py-2 tabular-nums text-slate-800 font-medium">
                                    {disc.authorizedUnits === ""
                                      ? "—"
                                      : disc.authorizedUnits}
                                  </td>
                                  <td className="px-3 py-2 tabular-nums text-slate-700">
                                    {disc.usedUnits ?? 0}
                                  </td>
                                  <td
                                    className={`px-3 py-2 font-semibold tabular-nums ${
                                      remaining === 0
                                        ? UNAUTHORIZED_TEXT
                                        : "text-emerald-700"
                                    }`}
                                  >
                                    {remaining == null ? "—" : remaining}
                                    {under ? (
                                      <span className="ml-1 text-[11px] font-medium text-amber-700">
                                        (Under)
                                      </span>
                                    ) : null}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </section>
  );
};

export default PatientAuthorizationsSection;
